import { useCallback, useEffect } from "react";

import { useDispatch } from "react-redux";

import { employeeUnavailabilityActions } from "@store/actions/onboarding/_employeeUnavailabilityActions";
import {
  EMPTY_EMPLOYEE_UNAVAILABILITIES,
  employeeUnavailabilityKey
} from "@store/reducers/_employeeUnavailabilityCache";

import { useReduxSelector } from "./useReduxSelector";

export function useEmployeeUnavailabilities(employeeId) {
  const dispatch = useDispatch();
  const enterpriseId = localStorage.getItem("enterpriseId");
  const normalizedEmployeeId = employeeId?.toString();
  const key = employeeUnavailabilityKey(enterpriseId, normalizedEmployeeId);
  const entry = useReduxSelector(
    ({ onboarding }) =>
      onboarding.employeeUnavailabilities[key] ||
      EMPTY_EMPLOYEE_UNAVAILABILITIES
  );

  useEffect(() => {
    if (
      !key ||
      entry.isLoading ||
      entry.error ||
      (entry.isLoaded && !entry.isDataOutdated)
    )
      return;
    // Coalesce a mutation callback and nearby socket events. Inactive entries stay stale.
    const timer = setTimeout(
      () => {
        dispatch(
          employeeUnavailabilityActions.loadData(
            enterpriseId,
            normalizedEmployeeId
          )
        );
      },
      entry.isDataOutdated ? 100 : 0
    );
    return () => clearTimeout(timer);
  }, [
    dispatch,
    enterpriseId,
    normalizedEmployeeId,
    key,
    entry.isLoaded,
    entry.isLoading,
    entry.isDataOutdated,
    entry.invalidationVersion,
    entry.error
  ]);

  const refresh = useCallback(() => {
    if (!key) return;
    dispatch(
      employeeUnavailabilityActions.invalidateData(
        enterpriseId,
        normalizedEmployeeId
      )
    );
  }, [dispatch, enterpriseId, normalizedEmployeeId, key]);

  const remove = useCallback(
    (id) => {
      if (!key) return;
      dispatch(
        employeeUnavailabilityActions.removeFromCache(
          enterpriseId,
          normalizedEmployeeId,
          id
        )
      );
    },
    [dispatch, enterpriseId, normalizedEmployeeId, key]
  );

  return { ...entry, refresh, remove };
}
